import {AspectRatio, ClipOverlay, EditorContextType, Overlay, OverlayType} from "../../reactvideoeditor/pro/types";
import {ASSET_TRACK_ID, EDITOR_MIME_TYPES, FPS, L_CUT_OFFSET, MAIN_TRACK_ID, REPLAY_TRACK_ID} from "../../constants";
import {
    AdjustWindowRequestElement,
    ContentType,
    EditMode,
    EDITOR_CONTENT_TYPE,
    EditWindowMode,
    WindowLandmark
} from "../../types/editorTypes";
import {KeyMomentCut, tempKeyMoment} from "../../types/keyMomentTypes";
import {EDITORIAL_ASSETS, EditorialAsset, INTERLEAVE_MODE} from "../../types/editorialAssetsTypes";
import { enqueueSnackbar } from 'notistack';

export const getOverlayUniqueId = (input = '') => {
  const MAX = BigInt(Number.MAX_SAFE_INTEGER);
  let hash = BigInt(0);

  // FNV-1a hash semplice
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * BigInt(1099511628211)) % MAX; // costante FNV prime
  }

  return Number(hash);
}

export const getVideoMetadata = (url: string) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata'; // Importante: scarica solo i metadati
        video.src = url;

        video.onloadedmetadata = () => {
            resolve({
                durationInSeconds: video.duration,
                dimensions: {width:video.videoWidth, height:video.videoHeight},
            });
            // Pulizia per liberare memoria
            video.remove();
        };

        video.onerror = () => reject("Errore nel caricamento del video");
    });
};

export function interpolate(...props: any[]): any{

}

export function useCurrentFrame(...props: any[]): any{

}

export function random(...props: any[]): any{

}

export function spring(...props: any[]): any{

}

export function useVideoConfig(...props: any[]): any{

}

export function Sequence(...props: any[]): any{

}

export function useCurrentScale(...props: any[]): any{

}

export function Img(...props: any[]): any{

}

export function Audio(...props: any[]): any{

}

export function OffthreadVideo(...props: any[]): any{

}

export function delayRender(...props: any[]): any{

}

export function continueRender(...props: any[]): any{

}

export function registerRoot(...props: any[]): any{

}

export function AbsoluteFill(...props: any[]): any{

}

export function Composition(...props: any[]): any{

}

export function loadFontFromInfo(...props: any[]): any{

}

export function getInfo(...props: any[]): any{

}

export interface PlayerAudioTrack {
    index: number;
    groupId: string;
    name: string;
    language?: string;
    default: boolean;
    autoselect: boolean;
    hasSeparatePlaylist: boolean;
}

export interface PlayerAudioTracksAvailableEvent {
    overlayId: number;
    tracks: PlayerAudioTrack[];
    selectedTrackIndex: number | null;
}

export interface PlayerAudioTrackChangedEvent {
    overlayId: number;
    trackIndex: number;
}

export interface PlayerBufferingChangeEvent {
    buffering: boolean;
}

export interface PlayerEventMap extends HTMLVideoElementEventMap {
    audiotracksavailable: PlayerAudioTracksAvailableEvent;
    audiotrackchanged: PlayerAudioTrackChangedEvent;
    bufferingchange: PlayerBufferingChangeEvent;
}

export type PlayerEventCallback<K extends keyof PlayerEventMap | string = keyof PlayerEventMap | string> =
    (payload: K extends keyof PlayerEventMap ? PlayerEventMap[K] : any) => void;

export interface VideoPlayerRef {
    play: () => void;
    pause: () => void;
    seekTo: (frame: number) => void;
    setAudioTrack: (overlayId: number, trackIndex: number) => void;
    getAvailableAudioTracks: (overlayId: number) => PlayerAudioTrack[];
    addEventListener: (event: keyof PlayerEventMap | string, callback: PlayerEventCallback | EventListener) => void;
    removeEventListener: (event: keyof PlayerEventMap | string, callback: PlayerEventCallback | EventListener) => void;
    getCurrentFrame: () => number;
    getCurrentTime: () => number;
    setVolume: (v: number) => void;
    getVolume: () => number;
    setMuted: (muted: boolean) => void;
    getMuted: () => boolean;
}


export type PlayerRef = VideoPlayerRef;
export type FontInfo = any;
export type GoogleFont = any;

export function isHLS(videoURL: string){
    return videoURL ? videoURL.includes('.m3u8') : null
}

export function getTopOverlay(overlays: Overlay[], type: OverlayType) {
    return overlays.filter(o => o.type === type).reduce((min:Overlay, obj) => obj.row < min.row ? obj : min, overlays[0]);
}

export function getActiveOverlays(overlays: Overlay[], type: OverlayType, currentFrame: number, margin: number): {activeOverlays: Overlay[], upcomingOverlays: Overlay[]} {
    const currentActive = overlays.filter(
        (overlay) => {
            return overlay.type === type &&
                currentFrame >= overlay.from &&
                currentFrame < overlay.from + overlay.durationInFrames
        }
    );

    const upcoming = overlays.filter(
        (overlay) =>
            overlay.type === type &&
            currentFrame + margin >= overlay.from &&
            currentFrame + margin < overlay.from + overlay.durationInFrames &&
            !currentActive.includes(overlay) // escludi quelli già attivi
    );

    return {activeOverlays: currentActive, upcomingOverlays: upcoming };
}

export function getTrackActiveOverlays(overlays: Overlay[], type: OverlayType, trackId: number, currentFrame: number, margin: number): {activeOverlays: Overlay[], upcomingOverlays: Overlay[]} {
    const currentActive = overlays.filter(
        (overlay) => {
            return overlay.type === type &&
                currentFrame >= overlay.from &&
                currentFrame < overlay.from + overlay.durationInFrames &&
                overlay.row === trackId
        }
    );

    const upcoming = overlays.filter(
        (overlay) =>
            overlay.type === type &&
            currentFrame + margin >= overlay.from &&
            currentFrame + margin < overlay.from + overlay.durationInFrames &&
            overlay.row === trackId &&
            !currentActive.includes(overlay)
    );

    return {activeOverlays: currentActive, upcomingOverlays: upcoming };
}

export function getFrameFromTimestamp(time: number){
    return Math.round(time * FPS)
}

export function getTimestampFromFrame(frame: number){
    return frame / FPS
}

export const WINDOW_EDIT_MODES = [
    {id: EditWindowMode.manual, label: 'Manual', disabled: false},
    {id: EditWindowMode.auto, label: 'Auto', disabled: false}
]

export const EDIT_MODES = [
    {id: EditMode.editor, label: 'Editor', disabled: false},
    {id: EditMode.preview, label: 'Preview', disabled: false}
]

function convertWindowLandmarks(contentType: ContentType, overlays: ClipOverlay[], windowLandmarks: WindowLandmark[]){
    const landmarks: [number, number][] = []
    for (const windowLandmark of windowLandmarks){
        for (const overlay of overlays){
            if(windowLandmark.frame >= overlay.from && windowLandmark.frame <= overlay.from + overlay.durationInFrames){
                landmarks.push(
                    //Nel caso in cui lavoro col key moment allora il tempo del landmark già concide con quello del cut perché c'è lo stream nell'editor
                    //Nel caso in cui lavoro con clip invece il tempo del ladmark va portato nel cut perché la timeline non è lunga quanto la stram. In questo caso fa calcolato di quanto
                    //offset sta spostato sulla destra rispetto all'inizio del segmento e va aggiunto il tempo stream effettivo da cui quel segmento parte
                    [contentType === EDITOR_CONTENT_TYPE.KEY_MOMENT ? windowLandmark.time : (windowLandmark.time - overlay.from / FPS) + (overlay.videoStartTime as number), windowLandmark.coordinate])
            }
        }
    }
    return landmarks;
}

export function mapOverlaysToEditElement(contentType: ContentType, overlays: ClipOverlay[], windowMode: EditWindowMode, windowLandmarks: WindowLandmark[], discriminator?: string): AdjustWindowRequestElement{
    const orderedOverlays = [...overlays].sort((a, b) => a.videoStartTime! - b.videoStartTime!);
    const trimPoints: [number, number][] = orderedOverlays.map(o => [o.videoStartTime!, o.videoStartTime! + o.durationInFrames / FPS])
    const trimLabels: string[] = orderedOverlays.map(o => (o.trim_label || '').toUpperCase())
    const landmarks = convertWindowLandmarks(contentType, overlays, windowLandmarks.filter(landmark => !landmark.isHidden));
    const hiddenLandmarks = convertWindowLandmarks(contentType, overlays, windowLandmarks.filter(landmark => landmark.isHidden));
    return {
        event_id: overlays[0].idEvent!,
        mode: windowMode,
        trim_points: trimPoints,
        trim_labels: trimLabels,
        landmarks: landmarks,
        hidden_landmarks: hiddenLandmarks,
        stream_reference: overlays[0].stream_reference,
        real_stream_reference: overlays[0].real_stream_reference,
        discriminator: discriminator
    }
}

export function groupBy<T, K extends keyof T>(array: T[], key: K): Record<string, T[]> {
    return array.reduce((result, item) => {
        const groupKey = String(item[key]);

        if (!result[groupKey]) {
            result[groupKey] = [];
        }

        result[groupKey].push(item);
        return result;
    }, {} as Record<string, T[]>);
}

export function getNumericAspectRatio(labelAspectRatio: AspectRatio){
    return parseFloat(labelAspectRatio.split(":")[0]) / parseFloat(labelAspectRatio.split(":")[1])
}

export function getCutsFromOverlay(contentType: string, keyMoments: tempKeyMoment[], overlays: ClipOverlay[], windowMode: EditWindowMode, windowLandmarks: WindowLandmark[]){
    const groupedByKeyMoments = groupBy(overlays, "idClip")
    const cuts: KeyMomentCut[] = []
    for (const keyMomentId in groupedByKeyMoments){
        const sameClipOverlay = groupedByKeyMoments[keyMomentId]
        const clipEditElement = mapOverlaysToEditElement(contentType, sameClipOverlay, windowMode, windowLandmarks)
        const keyMoment = keyMoments.find(km => km.id === keyMomentId)
        if (keyMoment){
            cuts.push ({
                id: keyMoment.cut.id,
                trim_points: clipEditElement.trim_points,
                landmarks: clipEditElement.landmarks,
                hidden_landmarks: clipEditElement.hidden_landmarks || [],
                coordinates: [],
                mode: windowMode,
                trim_labels: clipEditElement.trim_labels,
                stream_reference: clipEditElement.stream_reference,
                real_stream_reference: clipEditElement.real_stream_reference || 0,
                key_moment_id: keyMoment.id,
            })
        }
    }
    return cuts
}

/**
 * Trasla la posizione globale di un overlay di `deltaFrames`, portando con sé tutti i dati la cui
 * posizione è espressa nello stesso sistema di coordinate globale della timeline: i trim point
 * annidati in video_player_overlays e il marcatore l-cut (lcutMarkerFrame). Va usata ogni volta
 * che il `from` di un overlay viene spostato "in blocco" (ripple shift, riordino/sort, drag, ecc.):
 * se questi dati non vengono spostati insieme al segmento, restano ancorati alla vecchia posizione
 * e le barre/il box del marcatore nella UI finiscono fuori dal segmento (di fatto invisibili).
 */
export function shiftOverlayGlobalPosition<T extends Overlay>(overlay: T, deltaFrames: number): T {
    if (deltaFrames === 0) return overlay;

    const clip = overlay as unknown as ClipOverlay;
    const nestedOverlays = clip.video_player_overlays;
    const hasLcutMarker = clip.lcutMarkerFrame !== undefined;

    return {
        ...overlay,
        from: overlay.from + deltaFrames,
        ...(nestedOverlays ? {
            video_player_overlays: nestedOverlays.map(vp => ({ ...vp, from: vp.from + deltaFrames })),
        } : {}),
        ...(hasLcutMarker ? {
            lcutMarkerFrame: (clip.lcutMarkerFrame as number) + deltaFrames,
        } : {}),
    };
}

const rippleShiftOverlays = (
    overlays: Overlay[],
    thresholdFrame: number,
    deltaFrames: number,
    direction: 'left' | 'right',
    row?: number
): Overlay[] => {

    const computeShift = (overlay: Overlay): number => {
        const isPastThreshold = overlay.from >= thresholdFrame;
        const isRightRow = row === undefined || overlay.row === row;

        if (!isPastThreshold || !isRightRow) {
            return 0;
        }

        const shift = direction === 'right' ? deltaFrames : -deltaFrames;
        const newFrom = Math.max(thresholdFrame, overlay.from + shift);
        return newFrom - overlay.from;
    };

    const shiftById = new Map<number, number>();
    overlays.forEach((overlay) => shiftById.set(overlay.id, computeShift(overlay)));

    overlays.forEach((overlay) => {
        const empty = overlay as unknown as ClipOverlay;
        if (empty.type === OverlayType.EMPTY && empty.linkedAssetOverlayId !== undefined) {
            const emptyShift = shiftById.get(overlay.id) ?? 0;
            if (emptyShift !== 0) {
                shiftById.set(empty.linkedAssetOverlayId, emptyShift);
            }
        }
    });

    return overlays.map((overlay) =>
        shiftOverlayGlobalPosition(overlay, shiftById.get(overlay.id) ?? 0)
    );
};

const getFirstOverlay = (overlays: Overlay[], row?: number) => {
    if (!overlays || overlays.length === 0) return null;

    // Filtra per riga se il parametro è stato passato
    const filtered = row !== undefined ? overlays.filter(o => o.row === row) : overlays;
    if (filtered.length === 0) return null;

    return filtered.reduce((prev, current) => {
        return (prev.from < current.from) ? prev : current;
    });
};

const getLastOverlay = (overlays: Overlay[], row?: number) => {
    if (!overlays || overlays.length === 0) return null;

    // Filtra per riga se il parametro è stato passato
    const filtered = row !== undefined ? overlays.filter(o => o.row === row) : overlays;
    if (filtered.length === 0) return null;

    return filtered.reduce((prev, current) => {
        return (prev.from >= current.from) ? prev : current;
    });
};

const getFirstOverlayAfter = (overlays: Overlay[], targetFrame: number, row?: number) => {
    if (!overlays || overlays.length === 0) return null;

    // Filtra per targetFrame E per riga (se specificata)
    const futureOverlays = overlays.filter(overlay =>
        overlay.from > targetFrame && (row === undefined || overlay.row === row)
    );

    if (futureOverlays.length === 0) return null;

    return futureOverlays.reduce((prev, current) => {
        return (prev.from < current.from) ? prev : current;
    });
};

const getLastOverlayBefore = (overlays: Overlay[], targetFrame: number, row?: number) => {
    if (!overlays || overlays.length === 0) return null;

    // Filtra per targetFrame E per riga (se specificata)
    const pastOverlays = overlays.filter(overlay =>
        overlay.from < targetFrame && (row === undefined || overlay.row === row)
    );

    if (pastOverlays.length === 0) return null;

    return pastOverlays.reduce((prev, current) => {
        return (prev.from > current.from) ? prev : current;
    });
};

export const getOverlaysByRow = (overlays: Overlay[], row: number): Overlay[] => {
    // Se l'array è vuoto o indefinito, restituiamo un array vuoto in modo sicuro
    if (!overlays || overlays.length === 0) return [];

    // Filtriamo e restituiamo solo gli overlay che appartengono alla riga specificata
    return overlays.filter(overlay => overlay.row === row);
};

export const isReplaySegment = (overlay: Overlay) => overlay.type === OverlayType.VIDEO && (overlay as any).trim_label?.toUpperCase() === 'REPLAY';

export const canResizeReplaySegment = (editorContentType: ContentType, lCut: boolean) =>
    editorContentType === EDITOR_CONTENT_TYPE.KEY_MOMENT || !lCut;

export const getReplayFrameFromLcutReference = (overlay: Overlay, offsetSeconds: number = L_CUT_OFFSET): number | null => {
    const clip = overlay as any;
    if (clip.lcut_stream_reference === undefined || clip.lcut_stream_reference === null) {
        return null;
    }
    const videoStartTime = clip.videoStartTime ?? 0;
    return Math.round(
        (clip.lcut_stream_reference - videoStartTime) * FPS +
            offsetSeconds * FPS +
            overlay.from
    );
};

export const getCurrentLCutOffsetSeconds = (replay: Overlay, allOverlays: Overlay[]): number => {
    const candidateTrimPoints = allOverlays.filter((o) => o.idClip === replay.idClip);
    const linkedTrimPoint = findLcutReferenceEntry(candidateTrimPoints, replay.from);
    if (!linkedTrimPoint) return L_CUT_OFFSET;

    const clip = linkedTrimPoint as any;
    if (clip.lcut_stream_reference === undefined || clip.lcut_stream_reference === null) {
        return L_CUT_OFFSET;
    }

    const videoStartTime = clip.videoStartTime ?? 0;
    const rawOffsetSeconds = (replay.from - linkedTrimPoint.from) / FPS - (clip.lcut_stream_reference - videoStartTime);
    return Math.max(0, rawOffsetSeconds);
};

export const applyLCutOffsetsToOverlays = (overlays: Overlay[], lCutOffsets: Record<string, number>): Overlay[] => {
    let workingOverlays = overlays;
    let hasChanges = false;

    for (const originalReplay of overlays.filter(isReplaySegment)) {
        const replay = workingOverlays.find((o) => o.id === originalReplay.id);
        if (!replay) continue;

        const candidateTrimPoints = workingOverlays.filter((o) => o.idClip === replay.idClip);
        const linkedTrimPoint = findLcutReferenceEntry(candidateTrimPoints, replay.from);
        if (!linkedTrimPoint) continue;

        const offsetSeconds = lCutOffsets[String(replay.id)] ?? L_CUT_OFFSET;
        const newFrom = getReplayFrameFromLcutReference(linkedTrimPoint, offsetSeconds);
        if (newFrom !== null && newFrom !== replay.from) {
            workingOverlays = workingOverlays.map((o) =>
                o.id === replay.id ? { ...o, from: newFrom } : o
            );
            hasChanges = true;
        }

        const updatedReplay = workingOverlays.find((o) => o.id === replay.id) as Overlay;

        if (
            (linkedTrimPoint as any).lcutMarkerFrame !== updatedReplay.from ||
            (linkedTrimPoint as any).lcutMarkerDurationInFrames !== updatedReplay.durationInFrames
        ) {
            workingOverlays = workingOverlays.map((o) =>
                o.id === linkedTrimPoint.id
                    ? { ...o, lcutMarkerFrame: updatedReplay.from, lcutMarkerDurationInFrames: updatedReplay.durationInFrames }
                    : o
            );
            hasChanges = true;
        }

        const replayEnd = updatedReplay.from + updatedReplay.durationInFrames;
        const linkedTrimPointEnd = linkedTrimPoint.from + linkedTrimPoint.durationInFrames;

        if (replayEnd > linkedTrimPointEnd) {
            const deltaFrames = replayEnd - linkedTrimPointEnd;

            workingOverlays = workingOverlays.map((o) =>
                o.id === linkedTrimPoint.id ? { ...o, durationInFrames: o.durationInFrames + deltaFrames } : o
            );

            workingOverlays = rippleShiftOverlays(
                workingOverlays,
                linkedTrimPointEnd,
                deltaFrames,
                'right'
            ).map((o) => (o.id === updatedReplay.id ? updatedReplay : o));

            hasChanges = true;
        }
    }

    return hasChanges ? workingOverlays : overlays;
};

export const repositionLinkedReplays = (overlays: Overlay[], lCutOffsets: Record<string, number>): Overlay[] => {
    let workingOverlays = overlays;
    let hasChanges = false;

    for (const originalReplay of overlays.filter(isReplaySegment)) {
        const replay = workingOverlays.find((o) => o.id === originalReplay.id);
        if (!replay) continue;

        const candidateTrimPoints = workingOverlays.filter((o) => o.idClip === replay.idClip);
        const linkedTrimPoint = findLcutReferenceEntry(candidateTrimPoints, replay.from);
        if (!linkedTrimPoint) continue;

        const offsetSeconds = lCutOffsets[String(replay.id)] ?? L_CUT_OFFSET;
        const rawNewFrom = getReplayFrameFromLcutReference(linkedTrimPoint, offsetSeconds);
        const minFrame = getReplayFrameFromLcutReference(linkedTrimPoint, 0);
        const maxFrame = getReplayMaxFromFrame(replay, linkedTrimPoint);
        const newFrom = rawNewFrom === null
            ? null
            : minFrame !== null
                ? Math.max(Math.min(rawNewFrom, maxFrame), minFrame)
                : Math.min(rawNewFrom, maxFrame);
        if (newFrom !== null && newFrom !== replay.from) {
            workingOverlays = workingOverlays.map((o) =>
                o.id === replay.id ? { ...o, from: newFrom } : o
            );
            hasChanges = true;
        }

        const updatedReplay = workingOverlays.find((o) => o.id === replay.id) as Overlay;

        if (
            (linkedTrimPoint as any).lcutMarkerFrame !== updatedReplay.from ||
            (linkedTrimPoint as any).lcutMarkerDurationInFrames !== updatedReplay.durationInFrames
        ) {
            workingOverlays = workingOverlays.map((o) =>
                o.id === linkedTrimPoint.id
                    ? { ...o, lcutMarkerFrame: updatedReplay.from, lcutMarkerDurationInFrames: updatedReplay.durationInFrames }
                    : o
            );
            hasChanges = true;
        }
    }

    return hasChanges ? workingOverlays : overlays;
};

export const isVarSegment = (overlay: Overlay) => overlay.type === OverlayType.VIDEO && (overlay as any).trim_label?.toUpperCase() === 'VAR';

// Un replay/l-cut si può agganciare a QUALSIASI tipo di evento (goal, cartellino, VAR check, ecc.):
// questi due tipi sono gli unici che non possono fare da "evento di riferimento" per un altro replay.
const isExcludedFromLcutReference = (overlay: Overlay) => isReplaySegment(overlay) || isVarSegment(overlay);

export function findLcutReferenceEntry<T extends Overlay>(entries: T[], targetFrame: number): T | undefined {
    const eligible = entries.filter((entry) => !isExcludedFromLcutReference(entry));

    const preceding = eligible
        .filter((entry) => entry.from < targetFrame)
        .reduce<T | undefined>((latest, entry) => (!latest || entry.from > latest.from ? entry : latest), undefined);
    if (preceding) return preceding;
    return eligible.reduce<T | undefined>((closest, entry) => (!closest || entry.from < closest.from ? entry : closest), undefined);
}

export const getReplayMaxFromFrame = (replay: Overlay, linkedTrimPoint: Overlay): number => {
    const clip = linkedTrimPoint as ClipOverlay;
    const nestedEntries = clip.video_player_overlays;

    if (Array.isArray(nestedEntries) && nestedEntries.length > 0) {
        const referenceEntry = (clip.lcutStretchedEntryId !== undefined
            ? nestedEntries.find((vp) => vp.id === clip.lcutStretchedEntryId)
            : undefined) ?? findLcutReferenceEntry(nestedEntries, replay.from);

        if (referenceEntry) {
            return referenceEntry.from + referenceEntry.durationInFrames - replay.durationInFrames;
        }
    }

    return linkedTrimPoint.from + linkedTrimPoint.durationInFrames - replay.durationInFrames;
};

export function applyStretchToEntries<T extends { from: number; durationInFrames: number }>(
    entries: T[],
    referenceEntry: T,
    deltaFrames: number
): T[] {
    if (deltaFrames === 0) return entries;

    const referenceEnd = referenceEntry.from + referenceEntry.durationInFrames;

    return entries.map((entry) => {
        if (entry === referenceEntry) {
            return { ...entry, durationInFrames: entry.durationInFrames + deltaFrames };
        }
        if (entry.from >= referenceEnd) {
            return { ...entry, from: entry.from + deltaFrames };
        }
        return entry;
    });
}

export const deleteClipOverlaysWithGapClosing = (overlays: Overlay[], idsToRemove: number[]): Overlay[] => {
    let result = [...overlays];

    const mainTrackTargets = result
        .filter(overlay => idsToRemove.includes(overlay.id) && overlay.row === MAIN_TRACK_ID)
        .sort((a, b) => b.from - a.from); // destra→sinistra: gli shift non alterano le posizioni dei prossimi da rimuovere

    for (const target of mainTrackTargets) {
        result = rippleShiftOverlays(
            result.filter(overlay => overlay.id !== target.id),
            Math.round(target.from),
            Math.round(target.durationInFrames),
            "left"
        );
    }

    return result.filter(overlay => !idsToRemove.includes(overlay.id));
};

export const moveReplaySegmentsToReplayTrack = (overlays: Overlay[], offsetsByIdClip: Record<string, number> = {}): Overlay[] => {
    let newOverlays = [...overlays];

    const fatSegmentsWithReplay = newOverlays.filter(overlay =>
        overlay.type === OverlayType.VIDEO &&
        Array.isArray((overlay as ClipOverlay).video_player_overlays) &&
        (overlay as ClipOverlay).video_player_overlays!.some(isReplaySegment)
    ) as ClipOverlay[];

    for (const fatSegment of fatSegmentsWithReplay) {
        const mainIndex = newOverlays.findIndex(overlay => overlay.id === fatSegment.id);
        if (mainIndex === -1) continue;

        const currentMain = newOverlays[mainIndex] as ClipOverlay;
        const videoPlayerOverlays = currentMain.video_player_overlays || [];
        const replayEntry = videoPlayerOverlays.find(isReplaySegment);
        if (!replayEntry) continue;

        const remainingEntries = videoPlayerOverlays.filter(vp => vp !== replayEntry);
        const oldMainEnd = currentMain.from + currentMain.durationInFrames;

        // L'evento di riferimento non è necessariamente il primo del blocco fuso: con tanti trim
        // point fusi insieme è l'ultimo evento (non replay/VAR) che precede il replay da estrarre.
        const referenceEntry = findLcutReferenceEntry(remainingEntries, replayEntry.from);

        const naiveDuration = remainingEntries.reduce((sum, vp) => sum + vp.durationInFrames, 0);

        const replayOffsetSeconds = offsetsByIdClip[String((replayEntry as any).idClip)] ?? L_CUT_OFFSET;
        const lcutFrame = referenceEntry?.lcut_stream_reference !== undefined
            ? Math.round((referenceEntry.lcut_stream_reference - (referenceEntry.videoStartTime || 0)) * FPS + (replayOffsetSeconds * FPS) + referenceEntry.from)
            : Math.round(oldMainEnd - replayEntry.durationInFrames);

        const replayEnd = lcutFrame + replayEntry.durationInFrames;

        let alignedEntries = remainingEntries;
        let stretchFrames = 0;

        if (referenceEntry) {
            const referenceNaturalEnd = referenceEntry.from + referenceEntry.durationInFrames;
            // Il replay che si sposta a sinistra (offset minore) non deve mai accorciare
            // l'evento di riferimento: la durata si allunga solo se il replay lo supera
            // (overrun), non si riduce mai se il replay finisce prima della fine naturale.
            stretchFrames = Math.max(0, Math.round(replayEnd - referenceNaturalEnd));

            if (stretchFrames !== 0) {
                alignedEntries = applyStretchToEntries(remainingEntries, referenceEntry, stretchFrames);
            }
        }

        const newDurationInFrames = naiveDuration + stretchFrames;

        newOverlays[mainIndex] = {
            ...currentMain,
            durationInFrames: newDurationInFrames,
            video_player_overlays: alignedEntries,
            // Segna dove inizia e quanto dura il replay estratto, per disegnare il marcatore sul segmento fuso
            lcutMarkerFrame: lcutFrame,
            lcutMarkerDurationInFrames: replayEntry.durationInFrames,
            // Ricordiamo l'allungamento applicato e a quale entry, per poterlo annullare esattamente al ripristino
            lcutStretchFrames: stretchFrames !== 0 ? stretchFrames : undefined,
            lcutStretchedEntryId: stretchFrames !== 0 ? referenceEntry?.id : undefined,
        };

        const newMainEnd = currentMain.from + newDurationInFrames;
        const delta = oldMainEnd - newMainEnd;
        if (delta !== 0) {
            newOverlays = rippleShiftOverlays(
                newOverlays,
                Math.round(Math.min(oldMainEnd, newMainEnd)),
                Math.round(Math.abs(delta)),
                delta > 0 ? "left" : "right"
            );
        }

        // Ripristiniamo il replay come segmento indipendente sulla traccia dedicata
        newOverlays.push({
            ...replayEntry,
            row: REPLAY_TRACK_ID,
            from: lcutFrame,
            video_player_overlays: [{
                ...replayEntry,
                row: REPLAY_TRACK_ID,
                from: lcutFrame,
            }]
        } as ClipOverlay);
    }

    const hasSegmentToMove = newOverlays.filter(overlay => isReplaySegment(overlay) && overlay.row !== REPLAY_TRACK_ID);
    if (hasSegmentToMove.length === 0) return newOverlays;

    // =====================================================================
    // FASE 1: Rimozione dei replay dalla timeline principale
    // =====================================================================
    // Ordiniamo i replay da rimuovere DA DESTRA A SINISTRA (from decrescente).
    // In questo modo, quando eliminiamo un elemento e facciamo uno shift a sinistra,
    // modifichiamo solo le posizioni di ciò che sta "a destra", senza sballare
    // le coordinate dei prossimi replay che dobbiamo rimuovere (che stanno a sinistra).
    hasSegmentToMove.sort((a, b) => b.from - a.from);

    for (const replaySegment of hasSegmentToMove) {
        newOverlays = rippleShiftOverlays(
            newOverlays.filter(overlay => overlay.id !== replaySegment.id),
            Math.round(replaySegment.from),
            Math.round(replaySegment.durationInFrames),
            "left"
        );
    }

    // =====================================================================
    // FASE 2: Calcolo L-Cut e riposizionamento sulla traccia Replay
    // =====================================================================
    // Ora la timeline principale è continua (i buchi sono chiusi).
    // Dobbiamo riposizionare i replay, ma lo facciamo mappandoli al loro evento di riferimento
    // (qualunque tipo di evento collegato allo stesso idClip: goal, cartellino, VAR check, ecc.,
    // non solo goal).
    const replaysWithReferenceEvents = hasSegmentToMove.map(replay => {
        const referenceEvent = newOverlays.find(overlay =>
            overlay.idClip === replay.idClip &&
            overlay.row === MAIN_TRACK_ID &&
            overlay.type === OverlayType.VIDEO &&
            !isExcludedFromLcutReference(overlay)
        );
        return { replay, referenceEvent };
    }).filter(item => item.referenceEvent !== undefined);

    // Ordiniamo da SINISTRA A DESTRA in base alla posizione attuale dell'evento di riferimento.
    // Questo garantisce che se allunghiamo il primo evento (facendo uno shift a destra),
    // sposteremo correttamente in avanti il secondo, in modo che quando toccherà a lui
    // le sue coordinate "from" saranno perfettamente aggiornate.
    replaysWithReferenceEvents.sort((a, b) => a.referenceEvent!.from - b.referenceEvent!.from);

    for (const { replay, referenceEvent } of replaysWithReferenceEvents) {
        // Recuperiamo il segmento di riferimento aggiornato ad ogni iterazione,
        // poiché gli shift "right" delle iterazioni precedenti potrebbero averlo spostato.
        const mainSegmentIndex = newOverlays.findIndex(overlay => overlay.id === referenceEvent!.id);
        if (mainSegmentIndex === -1) continue;
        const mainSegment: any = newOverlays[mainSegmentIndex];

        // Calcoliamo la posizione dell'L-cut basandoci sulla reale posizione corrente del mainSegment
        const replayOffsetSeconds = offsetsByIdClip[String(replay.idClip)] ?? L_CUT_OFFSET;
        const lcut_stream_reference = mainSegment.lcut_stream_reference
            ? (mainSegment.lcut_stream_reference - mainSegment.videoStartTime) * FPS + (replayOffsetSeconds * FPS) + mainSegment.from
            : ((mainSegment.from + mainSegment.durationInFrames) - replay.durationInFrames) * FPS;

        const referenceEnd = mainSegment.from + mainSegment.durationInFrames;
        const replayEnd = lcut_stream_reference + replay.durationInFrames;

        // Se il replay esce dal margine destro dell'evento di riferimento, dobbiamo allungarlo
        if (Math.round(referenceEnd) < Math.round(replayEnd)) {
            let newDurationInFrames = Math.round(replayEnd - mainSegment.from);

            newOverlays = rippleShiftOverlays(
                newOverlays,
                Math.round(referenceEnd),
                Math.round(newDurationInFrames - mainSegment.durationInFrames),
                "right"
            );

            // Aggiorniamo la durata dell'evento di riferimento nell'array in modo sicuro
            const updatedIndex = newOverlays.findIndex(overlay => overlay.id === referenceEvent!.id);
            if(updatedIndex !== -1) {
                (newOverlays[updatedIndex] as any).durationInFrames = newDurationInFrames;
            }
        }

        // Finalmente inseriamo il replay sulla traccia dedicata
        // Ora possiamo posizionarlo ESATTAMENTE all'lcut_stream_reference
        // senza dover applicare hack di compensazione.
        newOverlays.push({
            ...replay,
            row: REPLAY_TRACK_ID,
            from: Math.round(lcut_stream_reference)
        });
    }

    return newOverlays;
};

export const moveReplaySegmentsToMainTrack = (overlays: Overlay[]): Overlay[] => {
    const hasSegmentToMove = overlays.filter(overlay => isReplaySegment(overlay) && overlay.row !== MAIN_TRACK_ID) as ClipOverlay[];
    if (hasSegmentToMove.length === 0) return overlays;
    let newOverlays = [...overlays];

    for (const replaySegment of hasSegmentToMove) {
        delete replaySegment.video_player_overlays;
        const mainSegment = newOverlays.find(overlay => overlay.idClip === replaySegment.idClip && overlay.row === MAIN_TRACK_ID && overlay.type === OverlayType.VIDEO) as ClipOverlay | undefined;
        if (!mainSegment) continue;

        const mainEnd = mainSegment.from + mainSegment.durationInFrames;
        const isFatSegment = Array.isArray(mainSegment.video_player_overlays);

        if (isFatSegment) {
            const storedStretch = mainSegment.lcutStretchFrames || 0;
            const entries = mainSegment.video_player_overlays || [];
            const referenceEntry = storedStretch !== 0
                ? entries.find(vp => vp.id === mainSegment.lcutStretchedEntryId)
                : undefined;
            const unstretchedEntries = referenceEntry
                ? applyStretchToEntries(entries, referenceEntry, -storedStretch)
                : entries;
            const naiveDurationWithoutReplay = unstretchedEntries.reduce((sum, vp) => sum + vp.durationInFrames, 0);
            const naiveEntriesEnd = mainSegment.from + naiveDurationWithoutReplay;
            const restoredDuration = naiveDurationWithoutReplay + replaySegment.durationInFrames;
            const restoredMainEnd = mainSegment.from + restoredDuration;

            const delta = restoredMainEnd - mainEnd;
            newOverlays = rippleShiftOverlays(
                newOverlays.filter(overlay => overlay.id !== replaySegment.id),
                Math.round(Math.min(mainEnd, restoredMainEnd)),
                Math.round(Math.abs(delta)),
                delta >= 0 ? "right" : "left"
            );

            const mainIndex = newOverlays.findIndex(overlay => overlay.id === mainSegment.id);
            if (mainIndex === -1) continue;
            const currentMain = newOverlays[mainIndex] as ClipOverlay;

            newOverlays[mainIndex] = {
                ...currentMain,
                durationInFrames: restoredDuration,
                video_player_overlays: [
                    ...unstretchedEntries,
                    { ...replaySegment, row: MAIN_TRACK_ID, from: Math.round(naiveEntriesEnd) },
                ],
                // Il replay è di nuovo annidato: marcatore e allungamento l-cut non hanno più senso
                lcutMarkerFrame: undefined,
                lcutMarkerDurationInFrames: undefined,
                lcutStretchFrames: undefined,
                lcutStretchedEntryId: undefined,
            };
        } else {
            // Segmento "flat" legacy: il replay torna a essere un overlay a sé sulla traccia principale
            newOverlays = rippleShiftOverlays(
                newOverlays.filter(overlay => overlay.id !== replaySegment.id),
                Math.round(mainEnd),
                Math.round(replaySegment.durationInFrames),
                "right"
            );
            newOverlays = [...newOverlays, { ...replaySegment, row: MAIN_TRACK_ID, from: Math.round(mainEnd) }];
        }
    }
    return newOverlays;
};

const getSubtype = (editorialAsset: EditorialAsset) => {
    let currentSubtype = OverlayType.ASSET
    switch(editorialAsset.placement_type){
        case EDITORIAL_ASSETS.POST_ROLL: currentSubtype = OverlayType.POST_ROLL; break;
        case EDITORIAL_ASSETS.PRE_ROLL: currentSubtype = OverlayType.PRE_ROLL; break;
        case EDITORIAL_ASSETS.INTERLEAVE: currentSubtype = OverlayType.INTERLEAVE; break;
        case EDITORIAL_ASSETS.CLOSING_COVER: currentSubtype = OverlayType.CLOSING_COVER; break;
        case EDITORIAL_ASSETS.OPENING_COVER: currentSubtype = OverlayType.OPENING_COVER; break;
    }
    return currentSubtype
}

function getStaticAssetOverlay(editorialAsset: EditorialAsset, overlayId: number): ClipOverlay{
    // Integer frames only: a fractional durationInFrames (e.g. 1.78s × 30 = 53.4) makes the
    // engine's end-of-clip watermark demand a frame past the last integer frame — a frame no
    // sweep ever produces — freezing playback in "buffering" on the clip's last frame.
    const durationInFrame = Math.round(editorialAsset.duration * FPS)
    const currentSubtype = getSubtype(editorialAsset)    

    return {
        id: overlayId,
        idClip: editorialAsset.id,
        groupId: undefined,
        type: OverlayType.VIDEO,
        subtype: currentSubtype,
        from: 0,
        videoStartTime: 0,
        width: 1920,
        height: 1080,
        top: 0,
        left: 0,
        row: MAIN_TRACK_ID,
        isDragging: false,
        rotation: 0,
        content: "",
        src: editorialAsset.preview_path,
        durationInFrames: durationInFrame,
        mediaSrcDuration: editorialAsset.duration,
        idEvent: editorialAsset.placement_type,
        mimeType: EDITOR_MIME_TYPES.MP4,
        styles: {
            objectFit: "contain",
        }
    }
}

function addPreRoll(overlays: Overlay[], editorialAsset: EditorialAsset, selectedEditorialAssets: EditorialAsset[]): Overlay[]{
    const preRollOverlay: ClipOverlay = getStaticAssetOverlay(editorialAsset, getOverlayUniqueId(editorialAsset.id + '_PRE_ROLL'))

    //Se c'è l'opening cover e questa ha margine sinistro l'empty ad esso associato deve essere accorciato e l'asset già messo spostato
    const openingCover = selectedEditorialAssets.find(ea => ea.placement_type === EDITORIAL_ASSETS.OPENING_COVER)
    if(openingCover && openingCover.parameters.technical_parameters.margin_sx > 0){
        const mainOverlays = getOverlaysByRow(overlays, MAIN_TRACK_ID) as ClipOverlay[]
        const assetOverlays = getOverlaysByRow(overlays, ASSET_TRACK_ID) as ClipOverlay[]
        const assetOverlay = assetOverlays.find(overlay => overlay.idClip === openingCover.id);
        if (assetOverlay) {
            const emptyOverlay = mainOverlays.find(overlay => overlay.linkedAssetOverlayId === assetOverlay.id && overlay.type === OverlayType.EMPTY)
            if(emptyOverlay){
                const shrinkValueInFrames = Math.round(openingCover.parameters.technical_parameters.margin_sx * FPS)
                emptyOverlay.durationInFrames = Math.round(emptyOverlay.durationInFrames - shrinkValueInFrames)
                overlays = rippleShiftOverlays(overlays, Math.round(emptyOverlay.from + emptyOverlay.durationInFrames) , shrinkValueInFrames, "left")
            }else{
                console.error("No empty elements found for editorialAsset.", openingCover);
            }
        }else{
            console.error("No asset elements found for editorialAsset.", openingCover);
        }
    }

    preRollOverlay.from = 0 //pre roll sempre prima elemento della timeline
    //Ci serve procurare spazio per il pre roll quindi shifto tutta la time line a destra di un valore pari alla sua durata
    const shiftedOverlays = rippleShiftOverlays(overlays, 0, Math.round(preRollOverlay.durationInFrames), "right")

    if(openingCover && openingCover.parameters.technical_parameters.margin_sx > 0) {
        const assetOverlay = shiftedOverlays.find(overlay => overlay.idClip === openingCover.id);
        const shrinkValueInFrames = Math.round(openingCover.parameters.technical_parameters.margin_sx * FPS)
        if(assetOverlay) assetOverlay.from = Math.round(assetOverlay.from - shrinkValueInFrames)
    }

    shiftedOverlays.push(preRollOverlay as any)

    return shiftedOverlays;
}

function addPostRoll(overlays: Overlay[], editorialAsset: EditorialAsset, selectedEditorialAssets: EditorialAsset[]): Overlay[]{
    //Se c'è la closing cover e questa ha margine destro l'empty ad esso associato deve essere acoorciato
    const closingCover = selectedEditorialAssets.find(ea => ea.placement_type === EDITORIAL_ASSETS.CLOSING_COVER)
    if(closingCover && closingCover.parameters.technical_parameters.margin_dx > 0){       
        const mainOverlays = getOverlaysByRow(overlays, MAIN_TRACK_ID) as ClipOverlay[]
        const assetOverlays = getOverlaysByRow(overlays, ASSET_TRACK_ID) as ClipOverlay[]
        const assetOverlay = assetOverlays.find(overlay => overlay.idClip === closingCover.id)
        if(assetOverlay){
            const emptyOverlay = mainOverlays.find(overlay => overlay.linkedAssetOverlayId === assetOverlay.id && overlay.type === OverlayType.EMPTY);
            if(emptyOverlay){
                emptyOverlay.durationInFrames = Math.round(emptyOverlay.durationInFrames - (closingCover.parameters.technical_parameters.margin_dx * FPS))
            }else{
                console.error("No empty elements found for editorialAsset.", closingCover);
            }
        }else{
            console.error("No asset element found for editorialAsset.", closingCover );
        }
    }

    const postRollOverlay: ClipOverlay = getStaticAssetOverlay(editorialAsset, getOverlayUniqueId(editorialAsset.id + '_POST_ROLL'))
    const lastOverlay = getLastOverlay(overlays, MAIN_TRACK_ID)
    //Il post roll è sempre ultimo elemento della timeline quindi se esiste l'ultimo elemento lo mettiamo dopo l'ultimo
    postRollOverlay.from = lastOverlay ? lastOverlay.from + lastOverlay.durationInFrames : 0
    return [...overlays, postRollOverlay as any]
}

function createEmptyOverlay(params: {
    id: number;
    idClip: string;
    assetOverlayId: number;
    from: number;
    src: string;
    durationInFrames: number;
    mediaSrcDuration: number;
    subtype: OverlayType;
}): ClipOverlay {
    return {
        id: params.id,
        idClip: params.idClip,
        groupId: undefined,
        type: OverlayType.EMPTY,
        from: Math.round(params.from),
        videoStartTime: 0,
        width: 1920,
        height: 1080,
        top: 0,
        left: 0,
        row: MAIN_TRACK_ID,
        isDragging: false,
        rotation: 0,
        content: "",
        src: params.src,
        mimeType: EDITOR_MIME_TYPES.WEBM,
        linkedAssetOverlayId: params.assetOverlayId,
        durationInFrames: Math.round(params.durationInFrames),
        mediaSrcDuration: params.mediaSrcDuration,
        idEvent: undefined,
        subtype: params.subtype,
        styles: {
            objectFit: "contain",
        },
    };
}

function createAssetOverlay(params: { id: number; idClip: string; eyebrow: string | undefined; from: number; src: string; durationInFrames: number; mediaSrcDuration: number; subtype: OverlayType, mode:string }): ClipOverlay {
    return {
        id: params.id,
        idClip: params.idClip,
        groupId: undefined,
        type: OverlayType.ASSET,
        from: Math.round(params.from),
        videoStartTime: 0,
        width: 1920,
        height: 1080,
        top: 0,
        left: 0,
        row: ASSET_TRACK_ID,
        isDragging: false,
        rotation: 0,
        content: "",
        eyebrow: params.eyebrow,
        src: params.src,
        mimeType: EDITOR_MIME_TYPES.WEBM,
        durationInFrames: Math.round(params.durationInFrames),
        mediaSrcDuration: params.mediaSrcDuration,
        idEvent: undefined,
        subtype: params.subtype,
        mode: params.mode,
        styles: {
            objectFit: "contain",
        },
    };
}

function addCover(overlays: Overlay[], editorialAsset: EditorialAsset, assetRenderParametersMap: Map<number, any>): Overlay[]{
    const isOpenCover = editorialAsset.placement_type === EDITORIAL_ASSETS.OPENING_COVER
    const mainTrackOverlays = getOverlaysByRow(overlays, MAIN_TRACK_ID)
    const basicOverlays = mainTrackOverlays.filter(overlay => overlay.type === OverlayType.VIDEO && !Boolean(overlay.subtype))
    //Se una cover di apertura cerco il primo video che non è empty ne grafica
    //Se una cover di chiura cerco l'ultimo video che non è empty ne grafica
    const refOverlay =  isOpenCover ? getFirstOverlay(basicOverlays) : getLastOverlay(basicOverlays)
    //Se una cover di apertura l'emtpy deve prendere il posto del primo video non empty e non grafica
    //Se una cover di chiusura l'emtpy deve inserire dopo l'ultimo video video non empty e non grafica(per questo ne viene somata la durata)
    const emptyFrom = refOverlay ? (isOpenCover ? refOverlay.from : refOverlay.from + refOverlay.durationInFrames ) : 0;
    let assetFrom = refOverlay ? (isOpenCover ? refOverlay.from : refOverlay.from + refOverlay.durationInFrames ): 0;
    //Se una cover di apertura bisogna capire se è possibile applicare il margine sinistro (prima dell'empty potrebbe esserci il nulla)
    //Se una cover di chiusura bisogna capire se è possibile applicare il margine destro (dopo dell'empty potrebbe esserci il nulla)
    const proximityOverlay = isOpenCover ? getLastOverlayBefore(mainTrackOverlays, emptyFrom) : getFirstOverlayAfter(mainTrackOverlays, refOverlay ? refOverlay.from : 0);
    let emptyOverlayDuration = editorialAsset.duration;

    // Identifichiamo cosa si trova fisicamente a SINISTRA e a DESTRA della copertina sulla timeline
    // - In apertura: proximityOverlay è a sx, refOverlay (il video principale) è a dx.
    // - In chiusura: refOverlay (il video principale) è a sx, proximityOverlay è a dx.
    const elementToTheLeft = isOpenCover ? proximityOverlay : refOverlay;
    const elementToTheRight = isOpenCover ? refOverlay : proximityOverlay;

    // GESTIONE MARGINE SINISTRO (Sovrapposizione con l'elemento precedente)
    if (editorialAsset.parameters.technical_parameters.margin_sx > 0 && elementToTheLeft) {
        emptyOverlayDuration = emptyOverlayDuration - editorialAsset.parameters.technical_parameters.margin_sx;
        // Dato che mi sovrappongo a sinistra, devo anticipare l'inizio della copertina (arretro assetFrom)
        assetFrom = assetFrom - editorialAsset.parameters.technical_parameters.margin_sx * FPS;
    }

    // GESTIONE MARGINE DESTRO (Sovrapposizione con l'elemento successivo)
    if (editorialAsset.parameters.technical_parameters.margin_dx > 0 && elementToTheRight) {
        emptyOverlayDuration = emptyOverlayDuration - editorialAsset.parameters.technical_parameters.margin_dx;
    }
    const emptyOverlayDurationInFrames = emptyOverlayDuration * FPS;
    const emptyOverlayId = getOverlayUniqueId(editorialAsset.id + '_EMPTY_ASSET_COVER');
    const assetOverlayId = getOverlayUniqueId(editorialAsset.id + '_ASSET_COVER');

    const findAssetInMap = Object.entries(assetRenderParametersMap).find(([key, value]) => String(editorialAsset.id) === String(key))?.[1];
    let assetSrc = editorialAsset.preview_path
    if(findAssetInMap?.url) {assetSrc = findAssetInMap.url;}
    const currentSubtype = getSubtype(editorialAsset)

    const emptyOverlay = createEmptyOverlay({
        id: emptyOverlayId,
        idClip: editorialAsset.id,
        assetOverlayId: assetOverlayId,
        from: emptyFrom,
        src: assetSrc,
        durationInFrames: emptyOverlayDurationInFrames,
        mediaSrcDuration: emptyOverlayDuration,
        subtype: currentSubtype,
    });

    const assetOverlay = createAssetOverlay({
        id: assetOverlayId,
        idClip: editorialAsset.id,
        eyebrow: undefined, 
        from: assetFrom,
        src: assetSrc,
        durationInFrames: editorialAsset.duration * FPS,
        mediaSrcDuration: editorialAsset.duration,
        subtype: currentSubtype,
        mode: ''
    });

    const shiftedOverlays = rippleShiftOverlays(overlays, Math.round(emptyFrom), Math.round(emptyOverlayDurationInFrames), "right")
    shiftedOverlays.push(...[assetOverlay as any, emptyOverlay as any])
    return shiftedOverlays
}

function addInterLeave(overlays: Overlay[], editorialAsset: EditorialAsset, assetRenderParametersMap: Map<number, any>): Overlay[] {
    const mainTrackOverlays = getOverlaysByRow(overlays, MAIN_TRACK_ID);
    const basicOverlays = mainTrackOverlays
        .filter(o => o.type === OverlayType.VIDEO && !Boolean(o.subtype))
        .sort((a, b) => a.from - b.from);

    const { margin_sx, margin_dx, offset, position, mode } = editorialAsset.parameters.technical_parameters;
    if (basicOverlays.length < 1 || (basicOverlays.length === 1 && !['sx','dx'].includes(position)))
        return overlays;
    
    const assetTrackOverlays = getOverlaysByRow(overlays, ASSET_TRACK_ID);
    const openingCover = assetTrackOverlays.find( o => o.subtype === OverlayType.OPENING_COVER ) ?? null;
    const closingCover = assetTrackOverlays.find( o => o.subtype === OverlayType.CLOSING_COVER ) ?? null;

    // --- Costruzione degli insertion points in base a `position` ---
    type InsertionPoint =
        | { kind: 'before_first'; firstId: number }
        | { kind: 'between';      leftId: number; rightId: number }
        | { kind: 'after_last';   lastId: number };

    const shouldSkipPoint = (left: Overlay, right: Overlay, mode: string): boolean => {
        switch (mode) {
            case INTERLEAVE_MODE.EVENT:
                return left.idEvent === right.idEvent;
            default:
                return left.idClip === right.idClip && left.idEvent === right.idEvent;
        }
    };
    
    const betweenPoints: InsertionPoint[] = basicOverlays.slice(0, -1).flatMap((left, i) => {
        const right = basicOverlays[i + 1];        
        if (shouldSkipPoint(left, right, mode)) return [];
        return [{
            kind: 'between' as const,
            leftId: left.id,
            rightId: right.id,
        }];
    });

    let insertionPoints: InsertionPoint[];

    if (position === 'sx' && offset === undefined) {
        insertionPoints = [
            { kind: 'before_first', firstId: basicOverlays[0].id },
            ...betweenPoints,
        ];
    } else if (position === 'dx' && offset === undefined ) {
        insertionPoints = [
            ...betweenPoints,
            { kind: 'after_last', lastId: basicOverlays[basicOverlays.length - 1].id },
        ];
    } else {
        insertionPoints = betweenPoints;
    }

    let currentOverlays = [...overlays];
    for (const [index, point] of insertionPoints.entries()) {
        const updatedBasicOverlays = getOverlaysByRow(currentOverlays, MAIN_TRACK_ID)
            .filter(o => o.type === OverlayType.VIDEO && !Boolean(o.subtype));
        let { offset } = editorialAsset.parameters.technical_parameters;
        let emptyFrom: number;

        if (point.kind === 'before_first') {
            const firstElement = updatedBasicOverlays.find(o => o.id === point.firstId);
            if (!firstElement) continue;

            if (openingCover) {
                const updatedOpeningCover = getOverlaysByRow(currentOverlays, ASSET_TRACK_ID)
                    .find(o => o.subtype === OverlayType.OPENING_COVER);
                emptyFrom = updatedOpeningCover
                    ? updatedOpeningCover.from + updatedOpeningCover.durationInFrames
                    : firstElement.from;
            } else {
                emptyFrom = firstElement.from;
            }
        } else if (point.kind === 'after_last') {
            const lastElement = updatedBasicOverlays.find(o => o.id === point.lastId);
            if (!lastElement) continue;

            if (closingCover) {
                const updatedClosingCover = getOverlaysByRow(currentOverlays, ASSET_TRACK_ID)
                    .find(o => o.subtype === OverlayType.CLOSING_COVER);
                emptyFrom = updatedClosingCover
                    ? updatedClosingCover.from
                    : lastElement.from + lastElement.durationInFrames;
            } else {
                emptyFrom = lastElement.from + lastElement.durationInFrames;
            }
        } else {
            const elementToTheLeft  = updatedBasicOverlays.find(o => o.id === point.leftId);
            const elementToTheRight = updatedBasicOverlays.find(o => o.id === point.rightId);            
            if (!elementToTheLeft || !elementToTheRight) continue;
            emptyFrom = elementToTheLeft.from + elementToTheLeft.durationInFrames;
        }

        // --- Calcolo assetFrom e durata empty ---
        let assetFrom = emptyFrom;
        let emptyOverlayDuration = editorialAsset.duration;
        let effectiveMarginSx = margin_sx;

        if (point.kind === 'before_first' && !openingCover) {
            effectiveMarginSx = 0;
        }

        if (offset !== undefined) {
            assetFrom = emptyFrom + Math.ceil(offset * FPS);
        } else {
            if (effectiveMarginSx > 0) {
                emptyOverlayDuration -= effectiveMarginSx;
                assetFrom = emptyFrom - Math.ceil(effectiveMarginSx * FPS);
            }
            if (margin_dx > 0) emptyOverlayDuration -= margin_dx;
        }

        if (offset !== undefined || margin_sx + margin_dx >= editorialAsset.duration) {
            emptyOverlayDuration = 0;
        }

        const emptyOverlayId = getOverlayUniqueId(editorialAsset.id + '_EMPTY_ASSET_MID_' + index);
        const assetOverlayId = getOverlayUniqueId(editorialAsset.id + '_ASSET_MID_' + index);
        
        const findAssetInMap = Object.entries(assetRenderParametersMap).find(([key, value]) => String(assetOverlayId) === String(key.split('_&_')[1]))?.[1];
        let assetSrc = editorialAsset.preview_path
        if(findAssetInMap?.url) assetSrc = findAssetInMap.url
        const currentSubtype = getSubtype(editorialAsset)

        const isEventMode = editorialAsset.parameters.technical_parameters.mode === INTERLEAVE_MODE.EVENT
        let candidateId: number | undefined;
        if ('firstId' in point) candidateId = point.firstId;
        else if ('leftId' in point) candidateId = point.leftId;
        else if ('rightId' in point) candidateId = (point as any).rightId;
        else if ('lastId' in point) candidateId = point.lastId;
        
        const currentElement: any = updatedBasicOverlays.find(o => o.id === candidateId);
        const assetOverlay = createAssetOverlay({
            id: assetOverlayId,
            idClip: editorialAsset.id,
            eyebrow: (isEventMode && currentElement !== undefined) ? currentElement.eyebrow : undefined,
            from: assetFrom,
            src: assetSrc,
            durationInFrames: editorialAsset.duration * FPS,
            mediaSrcDuration: editorialAsset.duration,
            subtype: currentSubtype,
            mode: (isEventMode)? INTERLEAVE_MODE.EVENT : INTERLEAVE_MODE.KEY_MOMENT
        });

        if(offset !== undefined || margin_sx + margin_dx === editorialAsset.duration ) emptyOverlayDuration = 0
        const emptyOverlay = createEmptyOverlay({
            id: emptyOverlayId,
            idClip: editorialAsset.id,
            assetOverlayId: assetOverlayId,
            from: emptyFrom,
            src: editorialAsset.preview_path,
            durationInFrames: emptyOverlayDuration * FPS,
            mediaSrcDuration: emptyOverlayDuration,
            subtype: currentSubtype,
        });

        const shiftedOverlays = rippleShiftOverlays(
            currentOverlays, 
            Math.round(emptyFrom), 
            (emptyOverlayDuration===0)?0:Math.floor(emptyOverlayDuration * FPS)+1, 
            "right"
        );
        shiftedOverlays.push(assetOverlay as any, emptyOverlay as any);
        currentOverlays = shiftedOverlays;
    }
    currentOverlays = currentOverlays.sort((a, b) => a.from - b.from)
    return currentOverlays;
}

interface AddAssetResult {
    changedOverlays: Overlay[];
    result: boolean;
}

export function addEditorialAsset(
    overlays: Overlay[],
    editorialAsset: EditorialAsset,
    selectedEditorialAssets: EditorialAsset[],
    assetRenderParametersMap?: Record<string, any>,
    setSelectedEditorialAssets?: (value: EditorialAsset[]) => void,
): AddAssetResult {
    try{
        switch (editorialAsset.placement_type) {
            case EDITORIAL_ASSETS.OPENING_COVER: {
                const selectedInterleave = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.INTERLEAVE);
                if (selectedInterleave) {
                    const parameters = selectedInterleave.parameters.technical_parameters
                    if(parameters && editorialAsset.parameters.technical_parameters.margin_dx >= 0 && parameters.position === 'sx' && parameters.margin_sx >= 0) {
                        enqueueSnackbar('Cannot add opening cover: cover has right margin and the selected interleave has left margin.');
                        return {changedOverlays: overlays, result:false};
                    }
                }
                return {changedOverlays: addCover(overlays, editorialAsset, (assetRenderParametersMap || {}) as any), result:true};
            }

            case EDITORIAL_ASSETS.CLOSING_COVER: {
                const selectedInterleave = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.INTERLEAVE);
                if (selectedInterleave) {
                    const parameters = selectedInterleave.parameters.technical_parameters
                    if(parameters && editorialAsset.parameters.technical_parameters.margin_sx >= 0 && parameters.position === 'dx' && parameters.margin_dx >= 0) {
                        enqueueSnackbar('Cannot add closing cover: cover has left margin and the selected interleave has right margin.');
                        return {changedOverlays: overlays, result:false};
                    }
                }
                return {changedOverlays: addCover(overlays, editorialAsset, (assetRenderParametersMap || {}) as any), result:true};
            }

            case EDITORIAL_ASSETS.INTERLEAVE:
                const selectedOpener = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.OPENING_COVER);
                if (selectedOpener) {
                    if(selectedOpener && editorialAsset.parameters.technical_parameters.position === 'sx' && editorialAsset.parameters.technical_parameters.margin_sx >= 0) {
                        if(editorialAsset.parameters.technical_parameters.mode === INTERLEAVE_MODE.EVENT){
                            if (setSelectedEditorialAssets)setSelectedEditorialAssets(selectedEditorialAssets!.filter((a) => a.placement_type !== EDITORIAL_ASSETS.OPENING_COVER)||[])
                            overlays = deleteCover(overlays, selectedOpener)
                        }
                        else { 
                            enqueueSnackbar('Cannot add interleave: opening cover has right margin and the selected interleave has left margin.');
                            return {changedOverlays: overlays, result:false};
                        }
                    }
                }
                const selectedCloser = selectedEditorialAssets.find((a) => a.placement_type === EDITORIAL_ASSETS.CLOSING_COVER);
                if (selectedCloser) {
                    if(selectedCloser && editorialAsset.parameters.technical_parameters.position === 'dx' && editorialAsset.parameters.technical_parameters.margin_dx >= 0) {
                        enqueueSnackbar('Cannot add interleave: closing cover has left margin and the selected interleave has right margin.');
                        return {changedOverlays: overlays, result:false};
                    }
                }
                return {changedOverlays: addInterLeave(overlays, editorialAsset, (assetRenderParametersMap || {}) as any), result:true};

            case EDITORIAL_ASSETS.PRE_ROLL:
                return {changedOverlays: addPreRoll(overlays, editorialAsset, selectedEditorialAssets), result:true};

            case EDITORIAL_ASSETS.POST_ROLL:
                return {changedOverlays: addPostRoll(overlays, editorialAsset, selectedEditorialAssets), result:true};

            default: return {changedOverlays: overlays, result:false};
        }
    }
    catch(e:any){
        return {changedOverlays: overlays, result:false};
    }
}

function deletePreRoll(overlays: Overlay[], editorialAsset: EditorialAsset, selectedEditorialAssets: EditorialAsset[]): Overlay[]{
    const openingCover = selectedEditorialAssets.find(ea => ea.placement_type === EDITORIAL_ASSETS.OPENING_COVER)
    if(openingCover && openingCover.parameters.technical_parameters.margin_sx > 0){
        const mainOverlays = getOverlaysByRow(overlays, MAIN_TRACK_ID) as ClipOverlay[]
        const assetOverlays = getOverlaysByRow(overlays, ASSET_TRACK_ID) as ClipOverlay[]
        const assetOverlay = assetOverlays.find(overlay => overlay.idClip === openingCover.id);
        if (assetOverlay) {
            const emptyOverlay = mainOverlays.find(overlay => overlay.linkedAssetOverlayId === assetOverlay.id && overlay.type === OverlayType.EMPTY)
            if(emptyOverlay){
                const shrinkValueInFrames = (openingCover.parameters.technical_parameters.margin_sx * FPS)
                overlays = rippleShiftOverlays(overlays, Math.round(emptyOverlay.from + emptyOverlay.durationInFrames), Math.round(shrinkValueInFrames), "right")
                emptyOverlay.durationInFrames = emptyOverlay.durationInFrames + shrinkValueInFrames
            }else{
                console.error("No empty elements found for editorialAsset.", openingCover);
            }
        }else{
            console.error("No asset elements found for editorialAsset.", openingCover);
        }
    }

    const targetGraphicOverlay = overlays.find(overlay => overlay.idClip === editorialAsset.id)
    if (targetGraphicOverlay) {
        const removedTargetOverlays = overlays.filter(overlay => overlay.idClip !== editorialAsset.id)
        return rippleShiftOverlays(removedTargetOverlays, Math.round(targetGraphicOverlay.from), Math.round(targetGraphicOverlay.durationInFrames), 'left')
    }else{
        console.error("No corresponding target asset overlay for editorial asset", editorialAsset)
        return overlays
    }
}

function deletePostRoll(overlays: Overlay[], editorialAsset: EditorialAsset, selectedEditorialAssets: EditorialAsset[]): Overlay[]{
    const closingCover = selectedEditorialAssets.find(ea => ea.placement_type === EDITORIAL_ASSETS.CLOSING_COVER)
    if(closingCover && closingCover.parameters.technical_parameters.margin_dx > 0){
        const mainOverlays = getOverlaysByRow(overlays, MAIN_TRACK_ID) as ClipOverlay[]
        const assetOverlays = getOverlaysByRow(overlays, ASSET_TRACK_ID) as ClipOverlay[]
        const assetOverlay = assetOverlays.find(overlay => overlay.idClip === closingCover.id)
        if(assetOverlay){
            const emptyOverlay = mainOverlays.find(overlay => overlay.linkedAssetOverlayId === assetOverlay.id && overlay.type === OverlayType.EMPTY);
            if(emptyOverlay){
                emptyOverlay.durationInFrames = emptyOverlay.durationInFrames + (closingCover.parameters.technical_parameters.margin_dx * FPS)
            }else{
                console.error("No empty elements found for editorialAsset.", closingCover);
            }
        }else{
            console.error("No asset element found for editorialAsset.", closingCover );
        }
    }


    //Post roll ultimo elemento della timeline non c'è bisogno di effettuare nessuno shift alla sua eliminazione
    const removedOverlay = overlays.find(overlay => overlay.idClip === editorialAsset.id)
    if(removedOverlay) {
        const updatedOverlays = overlays.filter(overlay => overlay.idClip !== editorialAsset.id)
        return rippleShiftOverlays(updatedOverlays, Math.round(removedOverlay.from), Math.round(removedOverlay.durationInFrames), 'left')
    } 
    return overlays
}

function deleteCover(overlays: Overlay[], editorialAsset: EditorialAsset): Overlay[]{
    //Va eleminato sia l'empty che la grafica collegate dalla rispettive traccia
    const targetEmptyOverlay: ClipOverlay | undefined = getOverlaysByRow(overlays, MAIN_TRACK_ID).find(overlay => overlay.idClip === editorialAsset.id) as any
    if (targetEmptyOverlay) {
        const targetAssetOverlay = getOverlaysByRow(overlays, ASSET_TRACK_ID).find(overlay => overlay.id === targetEmptyOverlay.linkedAssetOverlayId)
        const removedOverlays = overlays.filter(overlay => overlay.id !== targetEmptyOverlay?.id && overlay.id !== targetAssetOverlay?.id)
        return rippleShiftOverlays(removedOverlays, Math.round(targetEmptyOverlay.from), Math.round(targetEmptyOverlay.durationInFrames), 'left')
    }else{
        console.error("No corresponding target asset overlay for editorial asset", editorialAsset)
        return overlays
    }
}

function deleteInterleave(overlays: Overlay[], editorialAsset: EditorialAsset): Overlay[] {
    const targetAssetOverlays = getOverlaysByRow(overlays, ASSET_TRACK_ID).filter(overlay => String(overlay.subtype) === String(editorialAsset.placement_type)) as ClipOverlay[];
    if (targetAssetOverlays.length === 0) {
        console.error("No corresponding target asset overlay for editorial asset", editorialAsset);
        return overlays;
    }

    const sortedAssetOverlays = [...targetAssetOverlays].sort((a, b) => b.from - a.from);
    let currentOverlays = [...overlays];

    for (const assetOverlay of sortedAssetOverlays) {
        const targetEmptyOverlay = getOverlaysByRow(currentOverlays, MAIN_TRACK_ID).find(overlay => 'linkedAssetOverlayId' in overlay && overlay.linkedAssetOverlayId === assetOverlay.id);
        const filteredOverlays = currentOverlays.filter(overlay => overlay.id !== assetOverlay.id && overlay.id !== targetEmptyOverlay?.id);
        if (targetEmptyOverlay) {
            currentOverlays = rippleShiftOverlays(
                filteredOverlays, 
                Math.round(targetEmptyOverlay.from), 
                Math.round(targetEmptyOverlay.durationInFrames),
                'left'
            );
        } else {
            currentOverlays = filteredOverlays;
        }
    }

    return currentOverlays;
}

export function deleteEditorialAsset(overlays: Overlay[], editorialAsset: EditorialAsset, selectedEditorialAssets: EditorialAsset[]): Overlay[]{
    switch(editorialAsset.placement_type){
        case EDITORIAL_ASSETS.OPENING_COVER: return deleteCover(overlays, editorialAsset)
        case EDITORIAL_ASSETS.CLOSING_COVER: return deleteCover(overlays, editorialAsset)
        case EDITORIAL_ASSETS.INTERLEAVE: return deleteInterleave(overlays, editorialAsset)
        case EDITORIAL_ASSETS.PRE_ROLL: return deletePreRoll(overlays, editorialAsset, selectedEditorialAssets)
        case EDITORIAL_ASSETS.POST_ROLL: return deletePostRoll(overlays, editorialAsset, selectedEditorialAssets)
        default: return overlays
    }
}

export function findMinStartMaxEnd (pairs: [number, number][]): [number, number] | null {
    if (pairs.length === 0) return null;

    return [
        Math.min(...pairs.map(([first]) => first)),
        Math.max(...pairs.map(([, second]) => second)),
    ];
};